<template>
  <v-row>
    <v-col cols="12">
      <h2 class="mb-2">Set your will location</h2>
      <label class="text-subtitle-1"
        >Where are you storing your originally signed will?</label
      >
      <v-row no-gutters class="mt-6">
        <v-col cols="12" md="8">
          <TextField v-model="willLocation" :disabled="!!testator.dateStored" />
        </v-col>
        <v-col
          cols="12"
          md="4"
          class="d-flex align-center"
          style="height: 56px"
        >
          <GradientButton
            v-if="!locationSaved"
            :disabled="!!testator.dateStored || !willLocation"
            :title="'Save'"
            :small="true"
            :callout="'false'"
            class="ml-md-8"
            @gradientButtonClicked="updateTestator"
            :loading="buttonLoading"
          />
          <v-icon
            v-else
            :color="this.whitelabel.colors.leftPanelGreen"
            size="36"
            class="ml-md-8"
          >
            {{ icons.mdiCheckCircle }}
          </v-icon>
        </v-col>
        <v-col cols="12" md="8">
          <div v-if="showSuccess" class="location-success-banner">
            <span>Your location successfully set</span>
            <v-icon
              class="location-success-banner__close"
              color="white"
              @click="showSuccess = false"
            >
              {{ icons.mdiClose }}
            </v-icon>
          </div>
        </v-col>
      </v-row>
    </v-col>
    <v-col cols="12">
      <h2 class="mb-2">Who do we contact upon your passing?</h2>
      <label class="text-subtitle-1"
        >Who should your Executor contact to get possession of your original
        will?</label
      >
      <PeopleList
        ref="peopleList"
        :people="willLocationContactArray"
        :testator="testator"
        relation="WILL_LOCATION_CONTACT"
        :limit="1"
        class="people-list"
      />
      <GradientButton
        v-if="willLocationContactArray.length"
        :title="'Edit contact'"
        :small="true"
        :fill="false"
        @gradientButtonClicked="editContact"
      />
    </v-col>

    <ErrorSnackbar v-model="showError" />
  </v-row>
</template>

<script>
  import { mdiCheckCircle, mdiClose } from '@mdi/js';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import PeopleList from '../people/PeopleList';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { UPDATE_TESTATOR_MUTATION } from '../../constants/graphql/testators';

  export default {
    name: 'SetWillLocation',
    components: {
      ErrorSnackbar,
      PeopleList,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
      peopleRelations: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        icons: {
          mdiCheckCircle: mdiCheckCircle,
          mdiClose: mdiClose,
        },
        willLocation: this.testator.dateStored
          ? 'Old Mutual Wills Vault'
          : this.testator.willLocation,
        savedLocation: this.testator.willLocation,
        showSuccess: false,
      };
    },
    computed: {
      willLocationContactArray() {
        return this.peopleRelations.willLocationContact?.id
          ? [this.peopleRelations.willLocationContact]
          : [];
      },
      locationSaved() {
        return !!this.savedLocation && this.willLocation === this.savedLocation;
      },
      determineWillLocation() {
        if (!!this.testator.dateStored) {
          return 'Old Mutual Wills Vault';
        }
        if (!this.testator.willLocation) {
          return '';
        }
        return this.testator.willLocation.length > 22
          ? this.testator.willLocation.substring(0, 22) + '...'
          : this.testator.willLocation;
      },
    },
    methods: {
      editContact() {
        this.$refs.peopleList.showEditDialog(this.willLocationContactArray[0]);
      },
      updateTestator() {
        this.buttonLoading = true;
        const testatorInputDetails = { willLocation: this.willLocation };
        const id = this.testatorId;

        this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
          })
          .then(data => {
            this.buttonLoading = false;
            this.savedLocation = this.willLocation;
            this.showSuccess = true;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'will-location-successfully-updated',
              omuserid: this.$store.getters?.userId,
            });
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .people-list {
    max-width: 500px;
  }

  .location-success-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #009677;
    color: #ffffff;
    border-radius: 16px;
    padding: 14px 17px 14px 16px;
    font-size: 14px;
    font-weight: 400;

    &__close {
      cursor: pointer;

      &::after {
        display: none;
      }

      &:focus,
      &:hover {
        outline: none;
      }
    }
  }
</style>
